import { useEffect, useId, useState } from "react"

import type { Annotation } from "@/types"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"

import { cn } from "@/utils/cn"

import { OtherReviewerLabels } from "./OtherReviewerLabels"

interface NumericalHumanFeedbackButtonProps {
  annotation: Annotation | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  onExplanationChange: (explanation: string | null) => void
  otherUserAnnotations?: Annotation[]
  minValue: number
  maxValue: number
  readOnly?: boolean
  displayValue?: string | null
}

export const NumericalHumanFeedbackButton = ({
  annotation,
  onValueChange,
  onExplanationChange: _onExplanationChange,
  otherUserAnnotations,
  minValue,
  maxValue,
  readOnly = false,
  displayValue,
}: NumericalHumanFeedbackButtonProps) => {
  const inputId = useId()
  const currentValue = annotation?.ground_truth_float_value ?? null
  const [inputValue, setInputValue] = useState(
    currentValue !== null && currentValue !== undefined
      ? String(currentValue)
      : "",
  )

  useEffect(() => {
    setInputValue(
      currentValue !== null && currentValue !== undefined
        ? String(currentValue)
        : "",
    )
  }, [currentValue])

  const handleInputBlur = () => {
    if (readOnly) {
      return
    }
    const numValue = inputValue.trim() === "" ? null : Number(inputValue)

    if (inputValue.trim() === "") {
      onValueChange(null, null, null)
    } else if (
      !isNaN(numValue!) &&
      Number.isInteger(numValue!) &&
      numValue! >= minValue &&
      numValue! <= maxValue
    ) {
      onValueChange(numValue, null, null)
    } else {
      setInputValue(
        currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : "",
      )
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) {
      return
    }
    if (e.key === "Enter") {
      e.currentTarget.blur()
    }
  }

  const resolvedDisplayValue =
    displayValue !== undefined ? (displayValue ?? "") : inputValue

  return (
    <div className="flex flex-col items-start justify-start gap-3 w-full">
      <Label htmlFor={inputId}>Human Review</Label>
      <div className="flex items-start justify-start gap-1.5 w-full">
        <div className="flex flex-col gap-1">
          <Input
            id={inputId}
            type="text"
            value={readOnly ? resolvedDisplayValue : inputValue}
            onChange={
              readOnly ? undefined : (e) => setInputValue(e.target.value)
            }
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            readOnly={readOnly}
            placeholder={readOnly ? "" : `${minValue}-${maxValue}`}
            aria-label={
              readOnly
                ? "Computed score"
                : `Enter value between ${minValue} and ${maxValue}`
            }
            className={cn("w-12", readOnly && "cursor-default")}
          />
        </div>

        {!readOnly && (
          <OtherReviewerLabels
            annotations={otherUserAnnotations}
            renderValue={(otherAnnotation) => (
              <span className="text-sm font-medium text-coval-text">
                {otherAnnotation.ground_truth_float_value ?? "N/A"}
              </span>
            )}
          />
        )}
      </div>
    </div>
  )
}
